import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha, useTheme } from "@mui/material/styles";
import { buildMemberDetail, dayOffSuffix } from "../../../shared utils/personDetail";

export default function ActivityCandidateGroupCard({
  group,
  eligibleGroups = [],
  onAssignAll,
  onAssignSelected,
  onCreateGroup,
  createGroupLabel,
}) {
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [expanded, setExpanded] = useState(false);

  const theme = useTheme();

  const members = useMemo(() => {
    const list = Array.isArray(group?.members) ? group.members : [];
    return list.slice().sort((a, b) => {
      const aIsAdult = String(a?.personType).toUpperCase() === "ADULT";
      const bIsAdult = String(b?.personType).toUpperCase() === "ADULT";
      if (aIsAdult !== bIsAdult) return aIsAdult ? -1 : 1;
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
  }, [group?.members]);

  const counts = useMemo(() => {
    const adults = members.filter((m) => String(m?.personType).toUpperCase() === "ADULT").length;
    const total = members.length;
    return { adults, students: total - adults, total };
  }, [members]);

  const selectablePeople = members;

  const toggleSelected = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Add everyone matching `predicate` to the current selection (additive, so
  // "all students" + "all adults" together selects everyone).
  const addToSelection = (predicate) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      selectablePeople.filter(predicate).forEach((member) => {
        next.add(`${String(member?.personType).toUpperCase()}:${member?.personId}`);
      });
      return next;
    });
  };

  const selectAllStudents = () =>
    addToSelection((member) => String(member?.personType).toUpperCase() !== "ADULT");

  const selectAllAdults = () =>
    addToSelection((member) => String(member?.personType).toUpperCase() === "ADULT");

  const clearSelection = () => setSelectedKeys(new Set());

  const selectedPeople = useMemo(() => {
    if (!selectedKeys.size) return [];
    return selectablePeople.filter((member) =>
      selectedKeys.has(`${String(member?.personType).toUpperCase()}:${member?.personId}`)
    );
  }, [selectablePeople, selectedKeys]);

  // Once the list is expanded (or anyone is picked), the primary button acts on
  // the selection rather than the whole group.
  const assignSelectedMode = expanded || selectedKeys.size > 0;

  const hasUnassignedStudents = counts.students > 0;
  const statusLabel = hasUnassignedStudents ? "Students unplaced" : "Adults only";
  const statusColor = hasUnassignedStudents ? "warning" : "success";

  const sxForCard = useMemo(() => {
    if (hasUnassignedStudents) {
      return {
        borderColor: theme.palette.warning.main,
        bgcolor: alpha(theme.palette.warning.main, 0.08),
      };
    }

    return {
      borderColor: theme.palette.success.main,
      bgcolor: alpha(theme.palette.success.main, 0.08),
    };
  }, [hasUnassignedStudents, theme]);

  const title = useMemo(() => {
    return group?.productName || group?.groupName || "Candidates";
  }, [group?.productName, group?.groupName]);

  return (
    <Card variant="outlined" sx={{ height: "100%", ...sxForCard }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Stack spacing={0.5}>
            <Typography fontWeight={800}>{title}</Typography>

            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
              <Chip
                size="small"
                label={statusLabel}
                color={statusColor}
                variant="filled"
              />
              <Chip size="small" label={`Total ${counts.total}`} />
            </Stack>
          </Stack>
        </Stack>

        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {counts.adults} adults • {counts.students} students
        </Typography>

        {onCreateGroup && (
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={onCreateGroup}
            disabled={counts.total === 0}
          >
            {createGroupLabel || "Create group"}
          </Button>
        )}

        {counts.total > 0 && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id={`assign-group-${group?.productId || group?.productName}`}>
                  Assign to…
                </InputLabel>
                <Select
                  labelId={`assign-group-${group?.productId || group?.productName}`}
                  label="Assign to…"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                >
                  {eligibleGroups.length === 0 ? (
                    <MenuItem value="" disabled>
                      No available activity groups
                    </MenuItem>
                  ) : (
                    eligibleGroups.map((activityGroup) => (
                      <MenuItem key={activityGroup.groupId} value={String(activityGroup.groupId)}>
                        {activityGroup.name} • {activityGroup.members?.length || 0}/
                        {activityGroup.capacity ?? "—"}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {assignSelectedMode ? (
                <Button
                  variant="contained"
                  disabled={!selectedGroupId || selectedPeople.length === 0}
                  onClick={() => onAssignSelected?.(Number(selectedGroupId), selectedPeople)}
                >
                  Assign selected ({selectedPeople.length})
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={!selectedGroupId}
                  onClick={() => {
                    clearSelection();
                    onAssignAll?.(Number(selectedGroupId));
                  }}
                >
                  Assign all ({counts.total})
                </Button>
              )}
            </Stack>

            {expanded && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button size="small" onClick={selectAllStudents}>
                  Select all students
                </Button>

                <Button size="small" onClick={selectAllAdults}>
                  Select all adults
                </Button>

                <Button size="small" onClick={clearSelection} disabled={selectedKeys.size === 0}>
                  Clear selection
                </Button>
              </Stack>
            )}
          </Stack>
        )}

        <Divider sx={{ my: 1 }} />

        <Accordion
          elevation={0}
          disableGutters
          expanded={expanded}
          onChange={(_, isExpanded) => {
            setExpanded(isExpanded);
            if (!isExpanded) clearSelection();
          }}
          sx={{ "&:before": { display: "none" } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0, minHeight: 32, "& .MuiAccordionSummary-content": { my: 0 } }}
          >
            <Typography variant="body2" fontWeight={700}>
              People
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            {members.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                Nobody in this group.
              </Typography>
            ) : (
              <List dense disablePadding sx={{ mt: 0.25 }}>
                {members.map((member) => {
                  const type = String(member?.personType).toUpperCase();
                  const key = `${type}:${member?.personId}`;
                  const isAdult = type === "ADULT";
                  const checked = selectedKeys.has(key);
                  const detail = buildMemberDetail(member, isAdult);

                  return (
                    <ListItem
                      key={key}
                      disableGutters
                      sx={{
                        py: 0,
                        px: member.inOtherGroup ? 0.5 : 0,
                        borderRadius: 1,
                        ...(member.inOtherGroup ? { bgcolor: "#fff4e5" } : {}),
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                        {expanded ? (
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => toggleSelected(key)}
                          />
                        ) : (
                          <span style={{ width: 34 }} />
                        )}

                        <Typography
                          variant="body2"
                          sx={{ lineHeight: 1.2 }}
                        >
                          <Box component="span" fontWeight={isAdult ? 800 : 500}>
                            {member.name}
                            {dayOffSuffix(member)}
                          </Box>
                          {detail && (
                            <Box
                              component="span"
                              sx={{ opacity: 0.7 }}
                              title={member.bookingGroupName || undefined}
                            >
                              {` — ${detail}`}
                            </Box>
                          )}
                          {member.inOtherGroup && (
                            <Box
                              component="span"
                              sx={{ color: "warning.dark", fontWeight: 700 }}
                            >
                              {` · already in ${
                                member.otherGroupNames?.join(", ") || "another group"
                              }`}
                            </Box>
                          )}
                        </Typography>
                      </Stack>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
}